import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getPartner, servicePricing } from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { InputNumber, message, Table, Select, Input } from "antd";
import { Plus, X, Pencil, Check, Ban } from "lucide-react";
import ToggleTabs from "../../../../reusableUI/tap";
import secureLocalStorage from "react-secure-storage";
import { getLookupData } from "../../../../Service/lookup";

const ONBOARDING_FIRST_KEY = "pricingOnboardingFirst";
const MAX_FEE = 99999;

const INDIVIDUAL_SERVICES = [
  { name: "IV Fluid", displayName: "IV Fluid" },
  { name: "Suppository", displayName: "Suppository" },
  { name: "Catheter Care", displayName: "Catheter Care" },
  { name: "Wound Care", displayName: "Wound Care" },
  { name: "Tracheostomy", displayName: "Tracheostomy" },
  { name: "Pain Management", displayName: "Pain Management" },
  { name: "Continuous Ryle's Tube Aspiration", displayName: "Continuous Ryle's Tube" },
  { name: "Hourly Ryle's Tube Aspiration", displayName: "Hourly Ryle's Tube" },
];

const GENERAL_SERVICES_INFO = [
  "Personal Care",
  "Transfer",
  "Medication Management",
  "Vital Signs",
  "Injection",
  "Output",
];

const PHYSIO_WRAPPER_KEY = "physioService";

const ALL_SERVICE_TABS = [
  { label: "Nurse", value: "Nurse" },
  { label: "Physiotherapist", value: "Physiotherapist" },
  { label: "Doctor", value: "Doctor" },
];


const SUPPORTED_SECTIONS = new Set(["Nurse", "Physiotherapist"]);

const uuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const shiftFromShiftType = (shiftType) => {
  if (shiftType === "Morning") return "morning";
  if (shiftType === "Night") return "night";
  if (shiftType === "24H") return "24/7";
  return "";
};

// DB shift mapping to UI label
const shiftTypeFromShift = (shift) => {
  if (shift === "morning") return "Morning";
  if (shift === "night") return "Night";
  if (shift === "24/7") return "24H";
  return shift || "";
};

const safeArr = (v) => (Array.isArray(v) ? v : []);

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const roundCurrency = (value) => Number(toNumber(value).toFixed(2));

const pickFee = (obj) => toNumber(obj?.fee ?? obj?.fees ?? 0);

const clampInt = (v, { min = 0, max = MAX_FEE } = {}) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return Math.min(max, Math.max(min, i));
};

const normalizeDiscount = (discount) => {
  if (!discount) return null;

  if (typeof discount === "object" && discount?.type && discount?.value != null) {
    const t = discount.type === "fixed" ? "fixed" : "percentage";
    const v = Number(discount.value);
    if (!Number.isFinite(v) || v <= 0) return null;
    return { type: t, value: v };
  }

  if (typeof discount === "string") {
    const str = discount.trim();
    if (!str) return null;

    if (str.includes("%")) {
      const v = Number(str.replace("%", "").trim());
      if (!Number.isFinite(v) || v <= 0) return null;
      return { type: "percentage", value: v };
    }

    const numeric = Number(str.replace(/[^\d.]/g, ""));
    if (Number.isFinite(numeric) && numeric > 0) return { type: "fixed", value: numeric };
  }

  return null;
};

const computeFinalFee = (baseFee, discountObj) => {
  const fee = Number(baseFee || 0);
  if (!discountObj || !fee) return fee;

  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v <= 0) return fee;

  let final = fee;
  if (discountObj.type === "percentage") {
    const pct = Math.min(Math.max(v, 0), 100);
    final = fee - (fee * pct) / 100;
  } else {
    final = fee - v;
  }

  return roundCurrency(Math.max(0, final));
};

const formatDiscount = (discountObj) => {
  if (!discountObj) return "--";
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v <= 0) return "--";
  return discountObj.type === "percentage" ? `${v}%` : `₹${v}`;
};

const normalizeServiceToken = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/-/g, " ");

const extractPartnerServices = (partnerData) => {
  const raw = safeArr(partnerData?.services);
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      return item?.name || item?.label || item?.serviceType || item?.title || "";
    })
    .map((x) => String(x || "").trim())
    .filter(Boolean);
};

const readOnboardingFirst = () => {
  const v = secureLocalStorage.getItem(ONBOARDING_FIRST_KEY);
  return v === true || v === "true" || v === "1" || v === 1;
};
const resolvePricingSource = (pricingData) => {
  if (!pricingData || typeof pricingData !== "object") return null;
  if (pricingData?.pricingInfo && typeof pricingData.pricingInfo === "object") return pricingData.pricingInfo;
  return pricingData;
};

const hasAnyPricing = (src) => {
  if (!src) return false;
  const nurse = src?.nurseService;
  const physio =
    src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY];

  const nurseRegular = safeArr(nurse?.regularShifts?.slots).length;
  const nurseOneTime = safeArr(nurse?.oneTimeShifts?.services).length;

  const physioServices = safeArr(physio?.services).length;
  const physioAssessment = toNumber(physio?.assessmentFees ?? physio?.assessmentFee ?? 0);

  return nurseRegular > 0 || nurseOneTime > 0 || physioServices > 0 || physioAssessment > 0;
};

const buildTabsFromPricing = (src) => {
  const tabs = [];
  if (!src) return ALL_SERVICE_TABS;

  const nurse = src?.nurseService;
  const physio = src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY];
  const doctor = src?.doctorService;

  const nurseHas = safeArr(nurse?.regularShifts?.slots).length > 0 || safeArr(nurse?.oneTimeShifts?.services).length > 0;
  const physioHas = safeArr(physio?.services).length > 0 || toNumber(physio?.assessmentFees ?? physio?.assessmentFee ?? 0) > 0;
  const doctorHas = safeArr(doctor?.services).length > 0;

  if (nurseHas) tabs.push({ label: "Nurse", value: "Nurse" });
  if (physioHas) tabs.push({ label: "Physiotherapist", value: "Physiotherapist" });
  if (doctorHas) tabs.push({ label: "Doctor", value: "Doctor" });

  return tabs.length ? tabs : ALL_SERVICE_TABS;
};

const Pricing = ({ pricingData, readOnly: readOnlyProp = undefined }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const pricingSource = useMemo(() => resolvePricingSource(pricingData), [pricingData]);
  const pricingProvided = useMemo(() => hasAnyPricing(pricingSource), [pricingSource]);

  const readOnly = readOnlyProp ?? pricingProvided;
  const firstFromState = location.state?.first;

  const [onboardingFirst, setOnboardingFirst] = useState(() => {
    if (typeof firstFromState === "boolean") return firstFromState;
    return readOnboardingFirst();
  });

  const [hasPricingInDB, setHasPricingInDB] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serviceList, setServiceList] = useState([]);

  const [activeSection, setActiveSection] = useState("Nurse");
  const [nurseMode, setNurseMode] = useState("onetime");

  const [nurseRegularData, setNurseRegularData] = useState([]);
  const [nurseOneTimeData, setNurseOneTimeData] = useState([]);
  const [physioData, setPhysioData] = useState([]);
  const [doctorData, setDoctorData] = useState([]);

  const [physioServices, setPhysioServices] = useState([]);
  const [physioAssessmentFee, setPhysioAssessmentFee] = useState(0);

  // form (EDIT mode only)
  const [formShiftType, setFormShiftType] = useState("");
  const [formDuration, setFormDuration] = useState("");
  const [formService, setFormService] = useState("");
  const [formFee, setFormFee] = useState(null);

  const [formDiscountType, setFormDiscountType] = useState("percentage");
  const [formDiscountValue, setFormDiscountValue] = useState(null);
  const [formNotes, setFormNotes] = useState("");

  const [editingKey, setEditingKey] = useState(null);
  const [editingKind, setEditingKind] = useState(null);

  const isNurse = activeSection === "Nurse";
  const isRegular = isNurse && nurseMode === "regular";


  const isSupportedSection = readOnly ? true : SUPPORTED_SECTIONS.has(activeSection);


  useEffect(() => {
    if (typeof firstFromState === "boolean") {
      setOnboardingFirst(firstFromState);
      secureLocalStorage.setItem(ONBOARDING_FIRST_KEY, firstFromState ? "1" : "0");
    }

  }, [firstFromState]);

  const normalizeRow = (row) => {
    const baseFee = pickFee(row);
    const d = normalizeDiscount(row.discount);
    return {
      ...row,
      fee: baseFee,
      discount: d,
      finalFee: computeFinalFee(baseFee, d),
    };
  };


  const hydrateFromPricingData = useCallback(() => {
    const src = pricingSource;
    if (!src) {
      setNurseRegularData([]);
      setNurseOneTimeData([]);
      setPhysioData([]);
      setDoctorData([]);
      setHasPricingInDB(false);
      setLoading(false);
      return;
    }

    // Tabs/section default based on available pricing
    const tabs = buildTabsFromPricing(src);
    if (tabs.length === 1) setActiveSection(tabs[0].value);
    else if (!tabs.some((t) => t.value === activeSection)) setActiveSection(tabs[0]?.value || "Nurse");

    // Nurse
    const nurseNode = src?.nurseService || {};
    const regularSlots = safeArr(nurseNode?.regularShifts?.slots);
    const oneTimeServices = safeArr(nurseNode?.oneTimeShifts?.services);

    const regularTable = regularSlots.map((slot) => {
      const shiftType = shiftTypeFromShift(slot?.shift);
      const duration = String(slot?.type || "").trim();
      return normalizeRow({
        key: uuid(),
        kind: "regular",
        shiftType,
        duration: duration || "-",
        service: `${duration || ""} Duty - ${shiftType}`.trim(),
        fee: pickFee(slot),
        notes: slot?.notes || "",
        discount: slot?.discount || null,
        shift: slot?.shift || "",
        type: slot?.type || "",
      });
    });

    const oneTimeTable = oneTimeServices.map((srv) =>
      normalizeRow({
        key: uuid(),
        kind: "onetime",
        shiftType: "One Time",
        duration: "-",
        service: srv?.serviceType || srv?.name || srv?.label || "",
        fee: pickFee(srv),
        notes: srv?.notes || "",
        discount: srv?.discount || null,
        serviceType: srv?.serviceType || srv?.name || srv?.label || "",
        services: safeArr(srv?.services),
      })
    );

    setNurseRegularData(regularTable.filter((x) => x.shiftType && x.fee > 0));
    setNurseOneTimeData(oneTimeTable.filter((x) => x.service && x.fee > 0));

    const hasOneTime = oneTimeTable.filter((x) => x.service && x.fee > 0).length > 0;
    const hasRegular = regularTable.filter((x) => x.shiftType && x.fee > 0).length > 0;
    if (activeSection === "Nurse") {
      if (hasOneTime) setNurseMode("onetime");
      else if (hasRegular) setNurseMode("regular");
    }

    // Physio
    const physioNode =
      src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY] || {};

    const assessmentFeesNum = toNumber(
      physioNode?.assessmentFees ?? physioNode?.assessmentFee ?? 0
    );
    setPhysioAssessmentFee(assessmentFeesNum);

    const physioSrv = safeArr(physioNode?.services);
    const treatments = physioSrv
      .map((x) =>
        normalizeRow({
          key: uuid(),
          kind: "physio",
          shiftType: "Treatment",
          duration: "-",
          service: x?.name || x?.serviceType || x?.label || "",
          fee: pickFee(x),
          discount: x?.discount || null,
          notes: x?.notes || "",
          _id: x?._id,
        })
      )
      .filter((x) => x.service && x.fee > 0);

    const physioTable = [];
    if (assessmentFeesNum > 0) {
      physioTable.push(
        normalizeRow({
          key: uuid(),
          kind: "physio_assessment",
          shiftType: "Visit",
          duration: "-",
          service: "Assessment",
          fee: assessmentFeesNum,
          discount: null,
          notes: "",
        })
      );
    }
    physioTable.push(...treatments);
    setPhysioData(physioTable);


    setDoctorData([]);

    const exists = hasAnyPricing(src);
    setHasPricingInDB(exists);
    setLoading(false);
  }, [pricingSource, activeSection]);

  /** -----------------------------
   * EDIT MODE: fetch partner pricing + services
   * ----------------------------- */
  const getServices = async () => {
    try {
      const res = await getLookupData();
      if (res?.error === false && Array.isArray(res?.data)) {
        const list = res.data
          .filter((item) => item.type === "Physiotherapy Treatments")
          .map((item) => item.label || item.name || item.value)
          .filter(Boolean);
        setPhysioServices(list);
      }
    } catch (e) {
      console.error("lookup error", e);
    }
  };

  const fetchPartnerData = async () => {
    try {
      setLoading(true);
      const res = await getPartner();

      if (res?.error !== false) {
        message.error(res?.message || "Failed to load pricing data");
        return;
      }

      const data = res?.data || {};

      const nurseSlotsDB = safeArr(data?.pricingInfo?.nurseService?.regularShifts?.slots);
      const nurseOneTimeDB = safeArr(data?.pricingInfo?.nurseService?.oneTimeShifts?.services);

      const physioNodeDB =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.[PHYSIO_WRAPPER_KEY] ||
        {};

      const physioServicesDB = safeArr(physioNodeDB?.services);
      const physioAssessmentFeesDB = toNumber(
        physioNodeDB?.assessmentFees ?? physioNodeDB?.assessmentFee ?? 0
      );

      const pricingExists =
        nurseSlotsDB.length > 0 ||
        nurseOneTimeDB.length > 0 ||
        physioServicesDB.length > 0 ||
        physioAssessmentFeesDB > 0;

      setHasPricingInDB(pricingExists);

      const extractedServices = extractPartnerServices(data);
      setServiceList(extractedServices);

      const nurseNode = data?.pricingInfo?.nurseService || {};
      const regularShifts = safeArr(nurseNode?.regularShifts?.slots);
      const oneTimeShifts = safeArr(nurseNode?.oneTimeShifts?.services);

      const regularTableData = regularShifts
        .map((slot) => {
          const discountObj = normalizeDiscount(slot?.discount);
          const shiftType = shiftTypeFromShift(slot?.shift);
          const duration = slot?.type || "";

          return normalizeRow({
            key: uuid(),
            kind: "regular",
            shiftType,
            duration,
            service: `${duration || ""} Duty - ${shiftType}`.trim(),
            fee: pickFee(slot),
            notes: slot?.notes || "",
            discount: discountObj,
            shift: slot?.shift || "",
            type: slot?.type || "",
          });
        })
        .filter((row) => row.shiftType && row.fee > 0);

      const oneTimeTableData = oneTimeShifts
        .map((srv) => {
          const discountObj = normalizeDiscount(srv?.discount);
          return normalizeRow({
            key: uuid(),
            kind: "onetime",
            shiftType: "One Time",
            duration: "-",
            service: srv?.serviceType || srv?.name || srv?.label || "",
            fee: pickFee(srv),
            notes: srv?.notes || "",
            discount: discountObj,
            serviceType: srv?.serviceType || srv?.name || srv?.label || "",
            services: safeArr(srv?.services),
          });
        })
        .filter((row) => row.service && row.fee > 0);

      setNurseRegularData(regularTableData);
      setNurseOneTimeData(oneTimeTableData);

      const physioNode =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.[PHYSIO_WRAPPER_KEY] ||
        {};

      const assessmentFeesNum = toNumber(
        physioNode?.assessmentFees ?? physioNode?.assessmentFee ?? 0
      );
      setPhysioAssessmentFee(assessmentFeesNum);

      const physioSrv = safeArr(physioNode?.services);

      const treatments = physioSrv
        .map((x) => {
          const discountObj = normalizeDiscount(x?.discount);
          return normalizeRow({
            key: uuid(),
            kind: "physio",
            shiftType: "Treatment",
            duration: "-",
            service: x?.name || x?.serviceType || x?.label || "",
            fee: pickFee(x),
            discount: discountObj,
            notes: x?.notes || "",
            _id: x?._id,
          });
        })
        .filter((x) => x.service && x.fee > 0);

      const physioTable = [];
      if (assessmentFeesNum > 0) {
        physioTable.push(
          normalizeRow({
            key: uuid(),
            kind: "physio_assessment",
            shiftType: "Visit",
            duration: "-",
            service: "Assessment",
            fee: assessmentFeesNum,
            discount: null,
            notes: "",
          })
        );
      }

      physioTable.push(...treatments);
      setPhysioData(physioTable);

      setDoctorData([]);
    } catch (e) {
      console.error("fetchPartnerData error", e);
      message.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (readOnly) {
      hydrateFromPricingData();
      return;
    }

    fetchPartnerData();
    getServices();

  }, [readOnly, hydrateFromPricingData]);

  useEffect(() => {
    cancelEdit();

  }, [activeSection]);


  useEffect(() => {
    if (!isRegular) return;
    if (formShiftType === "24H") {
      if (formDuration !== "24H") setFormDuration("24H");
    } else {
      if (formDuration === "24H") setFormDuration("");
    }
  }, [isRegular, formShiftType, formDuration]);

  const serviceTabs = useMemo(() => {
    if (readOnly) return buildTabsFromPricing(pricingSource);

    if (!Array.isArray(serviceList) || serviceList.length === 0) return ALL_SERVICE_TABS;
    const set = new Set(serviceList.map(normalizeServiceToken));

    const hasNurse = set.has("nurse") || set.has("nursing assistant") || set.has("nursingassistant");
    const hasPhysio = set.has("physiotherapist") || set.has("physio") || set.has("physiotherapy");
    const hasDoctor = set.has("doctor visits") || set.has("doctor visit") || set.has("doctor");

    const tabs = [];
    if (hasNurse) tabs.push({ label: "Nurse", value: "Nurse" });
    if (hasPhysio) tabs.push({ label: "Physiotherapist", value: "Physiotherapist" });
    if (hasDoctor) tabs.push({ label: "Doctor", value: "Doctor" });

    return tabs.length ? tabs : ALL_SERVICE_TABS;
  }, [readOnly, pricingSource, serviceList]);

  useEffect(() => {
    if (serviceTabs.length === 1) {
      setActiveSection(serviceTabs[0].value);
      return;
    }
    if (!serviceTabs.some((t) => t.value === activeSection)) {
      setActiveSection(serviceTabs[0]?.value || "Nurse");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTabs]);

  const hasAnyLocalRows = useMemo(() => {
    return (
      nurseRegularData.length > 0 ||
      nurseOneTimeData.length > 0 ||
      physioData.length > 0 ||
      doctorData.length > 0
    );
  }, [nurseRegularData.length, nurseOneTimeData.length, physioData.length, doctorData.length]);

  const canProceed = useMemo(() => {
    return Boolean(onboardingFirst && (hasPricingInDB || hasAnyLocalRows));
  }, [onboardingFirst, hasPricingInDB, hasAnyLocalRows]);

  const resetForm = () => {
    setFormShiftType("");
    setFormDuration("");
    setFormService("");
    setFormFee(null);
    setFormDiscountType("percentage");
    setFormDiscountValue(null);
    setFormNotes("");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingKind(null);
    resetForm();
  };

  const validateDiscount = (baseFee) => {
    const fee = Number(baseFee || 0);
    const hasDisc = formDiscountValue != null && formDiscountValue !== "" && Number(formDiscountValue) > 0;
    if (!hasDisc) return true;

    const v = Number(formDiscountValue);

    if (formDiscountType === "percentage") {
      if (!Number.isFinite(v) || v <= 0) return true;
      if (!Number.isInteger(v)) {
        message.warning("Percentage discount must be a whole number (no decimals)");
        return false;
      }
      if (v >= 100) {
        message.warning("Percentage discount must be below 100");
        return false;
      }
      return true;
    }

    if (!Number.isFinite(v) || v <= 0) return true;
    if (!Number.isInteger(v)) {
      message.warning("Fixed discount must be a whole number (no decimals)");
      return false;
    }
    if (fee > 0 && v >= fee) {
      message.warning("Fixed discount must be less than Fee (not equal / not greater)");
      return false;
    }

    return true;
  };

  const getDiscountObjFromForm = () => {
    const v = Number(formDiscountValue);
    if (!Number.isFinite(v) || v <= 0) return null;

    if (formDiscountType === "percentage") {
      return { type: "percentage", value: Math.trunc(v) };
    }
    return { type: "fixed", value: Math.trunc(v) };
  };

  const renderDiscountField = () => {
    const fee = Number(formFee || 0);

    const maxPercent = 99;
    const maxFixed = fee > 0 ? Math.max(0, Math.trunc(fee) - 1) : MAX_FEE;

    return (
      <div className="pricing-field">
        <div className="pricing-label">Discount</div>
        <div className="discount-group">
          <InputNumber
            placeholder="0"
            value={formDiscountValue}
            onChange={(v) => {
              if (formDiscountType === "percentage") {
                setFormDiscountValue(clampInt(v, { min: 0, max: maxPercent }));
              } else {
                setFormDiscountValue(clampInt(v, { min: 0, max: maxFixed }));
              }
            }}
            min={0}
            max={formDiscountType === "percentage" ? maxPercent : maxFixed}
            precision={0}
            step={1}
            controls={false}
            size="small"
            className="discount-value"
            parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
            formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
            onKeyDown={(e) => {
              const allowed = [
                "Backspace",
                "Delete",
                "Tab",
                "Enter",
                "ArrowLeft",
                "ArrowRight",
                "Home",
                "End",
              ];
              if (allowed.includes(e.key)) return;
              if (e.ctrlKey || e.metaKey) return;
              if (!/^\d$/.test(e.key)) e.preventDefault();
            }}
          />
          <Select
            value={formDiscountType}
            onChange={(t) => {
              setFormDiscountType(t);
              setFormDiscountValue(null);
            }}
            size="small"
            className="discount-type"
            dropdownMatchSelectWidth={false}
          >
            <Select.Option value="percentage">%</Select.Option>
            <Select.Option value="fixed">₹</Select.Option>
          </Select>
        </div>
      </div>
    );
  };

  const handleAddOrUpdate = () => {
    if (readOnly) return;

    if (!isSupportedSection) {
      message.info(`${activeSection} pricing is coming soon`);
      return;
    }

    const feeInt = clampInt(formFee, { min: 0, max: MAX_FEE });

    if (feeInt == null || feeInt <= 0) {
      message.warning("Please enter valid Fee (max 5 digits)");
      return;
    }

    if (!validateDiscount(feeInt)) return;

    const discountObj = getDiscountObjFromForm();

    if (activeSection === "Nurse") {
      if (nurseMode === "regular") {
        if (!formShiftType) {
          message.warning("Please select Shift Type");
          return;
        }

        const durationFinal = formShiftType === "24H" ? "24H" : formDuration;

        if (formShiftType !== "24H" && !durationFinal) {
          message.warning("Please select Duration");
          return;
        }

        const exists = nurseRegularData.some((x) => {
          if (x.key === editingKey) return false;
          return x.shiftType === formShiftType && x.duration === durationFinal;
        });

        if (exists) {
          message.warning("This regular service already exists");
          return;
        }

        const row = normalizeRow({
          key: editingKey || uuid(),
          kind: "regular",
          shiftType: formShiftType,
          duration: durationFinal,
          service: `${durationFinal} Duty - ${formShiftType}`,
          fee: feeInt,
          discount: discountObj,
          notes: formNotes,
          shift: shiftFromShiftType(formShiftType),
          type: durationFinal,
        });

        if (editingKey && editingKind === "regular") {
          setNurseRegularData((prev) => prev.map((x) => (x.key === editingKey ? row : x)));
          cancelEdit();
        } else {
          setNurseRegularData((prev) => [...prev, row]);
          resetForm();
        }
        return;
      }

      if (!formService?.trim()) {
        message.warning("Please select Service");
        return;
      }

      const serviceName = formService.trim();
      const exists = nurseOneTimeData.some((x) => x.key !== editingKey && x.service === serviceName);
      if (exists) {
        message.warning("This one-time service already exists");
        return;
      }

      const row = normalizeRow({
        key: editingKey || uuid(),
        kind: "onetime",
        shiftType: "One Time",
        duration: "-",
        service: serviceName,
        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
        serviceType: serviceName,
      });

      if (editingKey && editingKind === "onetime") {
        setNurseOneTimeData((prev) => prev.map((x) => (x.key === editingKey ? row : x)));
        cancelEdit();
      } else {
        setNurseOneTimeData((prev) => [...prev, row]);
        resetForm();
      }
      return;
    }

    if (activeSection === "Physiotherapist") {
      if (!formService?.trim()) {
        message.warning("Please select Service");
        return;
      }

      const serviceName = formService.trim();

      if (serviceName === "Assessment") {
        const exists = physioData.some((x) => x.key !== editingKey && x.service === "Assessment");
        if (exists) {
          message.warning("Assessment already exists");
          return;
        }
      } else {
        const exists = physioData.some((x) => x.key !== editingKey && x.service === serviceName);
        if (exists) {
          message.warning("This service already exists");
          return;
        }
      }

      const row = normalizeRow({
        key: editingKey || uuid(),
        kind: serviceName === "Assessment" ? "physio_assessment" : "physio",
        shiftType: serviceName === "Assessment" ? "Visit" : "Treatment",
        duration: "-",
        service: serviceName,
        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
      });

      if (editingKey && (editingKind === "physio" || editingKind === "physio_assessment")) {
        setPhysioData((prev) => prev.map((x) => (x.key === editingKey ? row : x)));
        cancelEdit();
      } else {
        setPhysioData((prev) => [...prev, row]);
        resetForm();
      }
    }
  };

  const startEdit = (record) => {
    if (readOnly) return;
    if (!isSupportedSection) return;

    setEditingKey(record.key);
    setEditingKind(record.kind || null);

    if (activeSection === "Nurse") {
      setNurseMode(record.kind === "regular" ? "regular" : "onetime");
    }

    setFormShiftType(record.shiftType === "One Time" ? "" : record.shiftType || "");
    setFormDuration(record.duration === "-" ? "" : record.duration || "");
    setFormService(record.service || "");
    setFormFee(clampInt(record.fee, { min: 0, max: MAX_FEE }));
    setFormNotes(record.notes || "");

    const d = normalizeDiscount(record.discount);
    setFormDiscountType(d?.type || "percentage");
    setFormDiscountValue(d?.value != null ? clampInt(d.value, { min: 0, max: MAX_FEE }) : null);
  };

  const removeRow = (record) => {
    if (readOnly) return;
    if (!isSupportedSection) return;

    if (editingKey === record.key) cancelEdit();

    if (activeSection === "Nurse") {
      if (record.kind === "regular") {
        setNurseRegularData((prev) => prev.filter((x) => x.key !== record.key));
      } else {
        setNurseOneTimeData((prev) => prev.filter((x) => x.key !== record.key));
      }
      return;
    }

    if (activeSection === "Physiotherapist") {
      setPhysioData((prev) => prev.filter((x) => x.key !== record.key));
    }
  };

  const handleSaveSection = async () => {
    if (readOnly) return;

    if (!isSupportedSection) {
      message.info(`${activeSection} pricing is coming soon`);
      return;
    }

    setSaving(true);
    try {
      if (activeSection === "Nurse") {
        if (nurseMode === "regular") {
          if (nurseRegularData.length === 0) {
            message.warning("Please add at least one regular shift pricing item");
            return;
          }

          const regular = nurseRegularData.map((item) => ({
            shift: shiftFromShiftType(item.shiftType),
            type: item.duration,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount
              ? { type: item.discount.type, value: Number(item.discount.value) }
              : undefined,
          }));

          const payload = {
            dutyType: "regularShifts",
            nurseService: {
              regularShifts: { slots: regular },
            },
          };

          const resp = await servicePricing(payload);
          if (resp?.error === false) {
            message.success("Regular shifts pricing saved successfully!");
            secureLocalStorage.setItem("pricingDone", true);
            await fetchPartnerData();
          } else {
            message.error(resp?.message || "Failed to save regular shifts pricing");
          }
          return;
        }

        if (nurseOneTimeData.length === 0) {
          message.warning("Please add at least one one-time service pricing item");
          return;
        }

        const oneTime = nurseOneTimeData.map((item) => {
          const obj = {
            serviceType: item.service,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount
              ? { type: item.discount.type, value: Number(item.discount.value) }
              : undefined,
          };
          if (item.service === "General Services") obj.services = GENERAL_SERVICES_INFO;
          return obj;
        });

        const payload = {
          dutyType: "oneTimeShifts",
          nurseService: {
            oneTimeShifts: { services: oneTime },
          },
        };

        const resp = await servicePricing(payload);
        if (resp?.error === false) {
          message.success("One-time services pricing saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
        } else {
          message.error(resp?.message || "Failed to save one-time services pricing");
        }
        return;
      }

      if (activeSection === "Physiotherapist") {
        const assessmentRow = physioData.find((x) => x.service === "Assessment");
        const assessmentFees = assessmentRow
          ? clampInt(assessmentRow.fee, { min: 0, max: MAX_FEE }) || 0
          : clampInt(physioAssessmentFee, { min: 0, max: MAX_FEE }) || 0;

        const services = physioData
          .filter((x) => x.service !== "Assessment")
          .map((item) => ({
            name: item.service,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount
              ? { type: item.discount.type, value: Number(item.discount.value) }
              : undefined,
          }))
          .filter((x) => x.name && x.fee > 0);

        if (assessmentFees <= 0 && services.length === 0) {
          message.warning("Please add Assessment fee or at least one treatment pricing");
          return;
        }

        const payload = {
          dutyType: "physioService",
          [PHYSIO_WRAPPER_KEY]: { assessmentFees, services },
        };

        const resp = await servicePricing(payload);
        if (resp?.error === false) {
          message.success("Physiotherapist pricing saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
        } else {
          message.error(resp?.message || "Failed to save physiotherapist pricing");
        }
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  const activeData = useMemo(() => {
    if (activeSection === "Nurse") {
      return nurseMode === "regular" ? nurseRegularData : nurseOneTimeData;
    }
    if (activeSection === "Physiotherapist") return physioData;
    return doctorData;
  }, [activeSection, nurseMode, nurseRegularData, nurseOneTimeData, physioData, doctorData]);

  // ✅ Columns: remove Action column in READ ONLY
  const columns = useMemo(() => {
    const baseCols = [
      { title: "Shift Type", dataIndex: "shiftType", key: "shiftType", width: 140 },
      { title: "Duration", dataIndex: "duration", key: "duration", width: 120 },
      { title: "Service", dataIndex: "service", key: "service", width: 240 },
      {
        title: "Fee (₹)",
        dataIndex: "finalFee",
        key: "finalFee",
        width: 130,
        render: (_, record) => {
          const hasDiscount = record?.discount && Number(record.discount.value) > 0;
          return (
            <div className="leading-tight">
              <div className="font-medium text-gray-900">₹{record.finalFee ?? record.fee}</div>
              {hasDiscount && (
                <div className="text-[11px] text-gray-400 line-through">₹{record.fee}</div>
              )}
            </div>
          );
        },
      },
      {
        title: "Discount",
        dataIndex: "discount",
        key: "discount",
        width: 110,
        render: (d) => formatDiscount(normalizeDiscount(d)),
      },
      {
        title: "Notes",
        dataIndex: "notes",
        key: "notes",
        width: 180,
        render: (v) => (v ? v : "--"),
      },
    ];

    if (readOnly) return baseCols;

    return [
      ...baseCols,
      {
        title: "Action",
        key: "action",
        width: 100,
        fixed: "right",
        render: (_, record) => (
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-indigo-500 hover:text-indigo-700 disabled:opacity-50"
              onClick={() => startEdit(record)}
              disabled={saving}
              title="Edit"
            >
              <Pencil className="w-4 h-4" />
            </button>

            <button
              type="button"
              className="text-red-500 hover:text-red-700 disabled:opacity-50"
              onClick={() => removeRow(record)}
              disabled={saving}
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [readOnly, saving]);

  const renderServiceInput = () => {
    if (readOnly) return null;

    if (activeSection === "Nurse") {
      return (
        <Select
          placeholder="Select service"
          value={formService || undefined}
          onChange={setFormService}
          className="w-full pricing-input"
          showSearch
          size="small"
        >
          <Select.Option value="General Services">General Services</Select.Option>
          {INDIVIDUAL_SERVICES.map((s) => (
            <Select.Option key={s.name} value={s.name}>
              {s.displayName}
            </Select.Option>
          ))}
        </Select>
      );
    }

    if (activeSection === "Physiotherapist") {
      return (
        <Select
          placeholder="Select service"
          value={formService || undefined}
          onChange={setFormService}
          className="w-full pricing-input"
          showSearch
          size="small"
        >
          <Select.Option value="Assessment">Assessment</Select.Option>
          {physioServices.map((s) => (
            <Select.Option key={s} value={s}>
              {s}
            </Select.Option>
          ))}
        </Select>
      );
    }

    return null;
  };

  const gridClass = isNurse
    ? nurseMode === "regular"
      ? formShiftType === "24H"
        ? "grid-nurse-regular-nodur"
        : "grid-nurse-regular"
      : "grid-nurse-onetime"
    : "grid-generic-onetime";

  if (loading) return <Loader />;

  return (
    <div className="h-[90vh] bg-gray-50 w-full p-2 overflow-hidden font-['Poppins']">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { font-family: 'Poppins', sans-serif !important; }

        .card-shell{
          background: white;
          border-radius: 16px;
          border: 1px solid #9EA3FF;
          height: 100%;
          padding: 14px;
          box-shadow: 0 1px 2px rgba(16,24,40,0.06);
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .pricing-strip{
          border: 1px solid #E6E8F0;
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
          margin-bottom: 10px;
        }

        .pricing-strip__grid{
          padding: 10px 12px;
          background: #FBFCFF;
          display: grid;
          gap: 10px;
          align-items: end;
        }

        .pricing-strip__grid.grid-nurse-onetime,
        .pricing-strip__grid.grid-generic-onetime{
          grid-template-columns: 2.4fr 0.9fr 1.15fr auto;
        }

        .pricing-strip__grid.grid-nurse-regular{
          grid-template-columns: 1.05fr 1.05fr 0.9fr 1.15fr auto;
        }

        .pricing-strip__grid.grid-nurse-regular-nodur{
          grid-template-columns: 1.25fr 0.9fr 1.15fr auto;
        }

        .pricing-field{ min-width: 0; }

        .pricing-label{
          font-size: 12px;
          font-weight: 600;
          color: #6B7280;
          margin-bottom: 5px;
          line-height: 14px;
        }

        .pricing-input.ant-input,
        .pricing-input .ant-input,
        .pricing-input.ant-input-number,
        .pricing-input .ant-input-number-input,
        .pricing-input.ant-select .ant-select-selector{
          height: 32px !important;
          border-radius: 10px !important;
        }

        .pricing-input.ant-select .ant-select-selector{
          display: flex !important;
          align-items: center !important;
        }

        .discount-group {
          display: flex;
          align-items: center;
          width: 100%;
          height: 32px;
        }

        .discount-value { flex: 1; width: 100%; }
        .discount-value.ant-input-number {
          height: 32px !important;
          border-radius: 10px 0 0 10px !important;
          display: flex !important;
          align-items: center !important;
        }
        .discount-value .ant-input-number-input-wrap {
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
        }
        .discount-value .ant-input-number-input {
          height: 100% !important;
          line-height: 1 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }

        .discount-type { width: 58px !important; flex: 0 0 58px !important; }
        .discount-type.ant-select { height: 32px !important; }
        .discount-type .ant-select-selector {
          height: 100% !important;
          min-height: 32px !important;
          margin-left: -1px;
          border-radius: 0 10px 10px 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .discount-type .ant-select-selection-item {
          line-height: 30px !important;
        }

        .add-actions{
          display:flex;
          justify-content:flex-end;
          align-items:center;
          gap: 8px;
          height: 32px;
        }

        .pricing-plus{
          width: 32px;
          height: 32px;
          border-radius: 999px;
          background: #4F46E5;
          color: #fff;
          display:flex;
          align-items:center;
          justify-content:center;
          border: none;
          box-shadow: 0 8px 14px rgba(79,70,229,.16);
        }

        .pricing-plus:hover{ background: #4338CA; }

        .pricing-cancel{
          width: 32px;
          height: 32px;
          border-radius: 999px;
          background: #fff;
          border: 1px solid #E5E7EB;
          color: #6B7280;
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .notes-row{ grid-column: 1 / 2; }
        .grid-nurse-regular .notes-row{ grid-column: 1 / 3; }
        .grid-nurse-regular-nodur .notes-row{ grid-column: 1 / 2; }

        .pricing-strip__divider{ height: 1px; background: #EEF0F7; }

        .pricing-strip__title{
          padding: 9px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #6B7280;
          background: #fff;
          display:flex;
          align-items:center;
          justify-content:space-between;
        }

        .pricing-table .ant-table{
          border: 1px solid #E6E8F0;
          border-radius: 12px;
          overflow: hidden;
          font-size: 12px;
        }

        .pricing-table .ant-table-thead > tr > th{
          background: #F6F7FF !important;
          color: #7A7FB0 !important;
          font-weight: 600 !important;
          padding: 10px 12px !important;
          border-bottom: 1px solid #E6E8F0 !important;
        }

        .pricing-table .ant-table-tbody > tr > td{
          padding: 10px 12px !important;
          border-bottom: 1px solid #EEF0F7 !important;
          color: #374151;
        }

        .pricing-table .ant-table-tbody > tr:hover > td{
          background: #FAFBFF !important;
        }

        .edit-pill{
          font-size: 11px;
          padding: 4px 8px;
          border-radius: 999px;
          background: #EEF2FF;
          color: #4F46E5;
          font-weight: 600;
        }

        .coming-soon-box{
          border: 1px dashed #CBD5E1;
          background: #fff;
          border-radius: 14px;
          padding: 22px;
          color: #374151;
        }
      `}</style>

      <div className="card-shell">
        <div className="flex items-center justify-between gap-3 mb-3 shrink-0">
          <div className="w-[420px] max-w-full">
            {serviceTabs.length > 1 ? (
              <ToggleTabs options={serviceTabs} active={activeSection} setActive={setActiveSection} />
            ) : (
              <div className="text-[14px] font-semibold text-gray-800">
                {serviceTabs[0]?.label || activeSection}
              </div>
            )}
          </div>


        </div>

        {/* Nurse sub-tabs */}
        {isSupportedSection && isNurse && (
          <div className="flex justify-center mb-3 shrink-0">
            <div className="w-[460px] max-w-full">
              <ToggleTabs
                options={[
                  { label: "One-Time Service", value: "onetime" },
                  { label: "Regular Service", value: "regular" },
                ]}
                active={nurseMode}
                setActive={(v) => {
                  setNurseMode(v);
                  cancelEdit();
                }}
              />
            </div>
          </div>
        )}

        <h3 className="text-[13px] font-medium text-blue-600 mb-2 shrink-0">
          {isNurse ? "Nursing Details" : `${activeSection} Details`}
        </h3>

        <div className="flex-1 min-h-0 overflow-auto pr-1">
          {/* ✅ READ ONLY: only tables (no add/edit UI) */}
          {readOnly ? (
            <Table
              columns={columns}
              dataSource={activeData}
              pagination={false}
              rowKey="key"
              scroll={{ x: 900 }}
              locale={{
                emptyText: isNurse
                  ? nurseMode === "regular"
                    ? "No regular service pricing available"
                    : "No one-time service pricing available"
                  : `No ${activeSection} pricing available`,
              }}
              className="pricing-table"
            />
          ) : (
            <>
              {/* Unsupported sections: Coming soon (EDIT mode only) */}
              {!isSupportedSection ? (
                <div className="coming-soon-box">
                  <div className="text-[16px] font-semibold text-gray-900 mb-1">
                    {activeSection} — Coming soon
                  </div>
                  <div className="text-[13px] text-gray-600">
                    Pricing setup for <span className="font-medium">{activeSection}</span> will be available soon.
                  </div>
                </div>
              ) : (
                <>
                  {/* EDIT UI STRIP */}
                  <div className="pricing-strip">
                    <div className={`pricing-strip__grid ${gridClass}`}>
                      {/* Nurse one-time */}
                      {isNurse && nurseMode === "onetime" && (
                        <>
                          <div className="pricing-field">
                            <div className="pricing-label">Service</div>
                            {renderServiceInput()}
                          </div>

                          <div className="pricing-field">
                            <div className="pricing-label">Fee (in ₹)</div>
                            <InputNumber
                              placeholder="0"
                              value={formFee}
                              onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                              min={0}
                              max={MAX_FEE}
                              precision={0}
                              step={1}
                              className="w-full pricing-input"
                              controls={false}
                              size="small"
                              parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                              formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                            />
                          </div>

                          {renderDiscountField()}

                          <div className="pricing-field">
                            <div className="pricing-label opacity-0 select-none">Action</div>
                            <div className="add-actions">
                              {editingKey && (
                                <button onClick={cancelEdit} className="pricing-cancel" type="button" title="Cancel">
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={handleAddOrUpdate} className="pricing-plus" type="button" title={editingKey ? "Update" : "Add"}>
                                {editingKey ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          <div className="pricing-field notes-row">
                            <div className="pricing-label">Notes</div>
                            <Input
                              placeholder="Notes"
                              value={formNotes}
                              onChange={(e) => setFormNotes(e.target.value)}
                              className="w-full pricing-input"
                              size="small"
                            />
                          </div>
                        </>
                      )}

                      {/* Nurse regular */}
                      {isNurse && nurseMode === "regular" && (
                        <>
                          <div className="pricing-field">
                            <div className="pricing-label">Shift Type</div>
                            <Select
                              placeholder="Select"
                              value={formShiftType || undefined}
                              onChange={setFormShiftType}
                              className="w-full pricing-input"
                              size="small"
                            >
                              <Select.Option value="Morning">Morning</Select.Option>
                              <Select.Option value="Night">Night</Select.Option>
                              <Select.Option value="24H">24H</Select.Option>
                            </Select>
                          </div>

                          {formShiftType !== "24H" ? (
                            <div className="pricing-field">
                              <div className="pricing-label">Duration</div>
                              <Select
                                placeholder="Select"
                                value={formDuration || undefined}
                                onChange={setFormDuration}
                                className="w-full pricing-input"
                                size="small"
                              >
                                <Select.Option value="9H">9H</Select.Option>
                                <Select.Option value="12H">12H</Select.Option>
                              </Select>
                            </div>
                          ) : null}

                          <div className="pricing-field">
                            <div className="pricing-label">Fee (in ₹)</div>
                            <InputNumber
                              placeholder="0"
                              value={formFee}
                              onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                              min={0}
                              max={MAX_FEE}
                              precision={0}
                              step={1}
                              className="w-full pricing-input"
                              controls={false}
                              size="small"
                              parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                              formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                            />
                          </div>

                          {renderDiscountField()}

                          <div className="pricing-field">
                            <div className="pricing-label opacity-0 select-none">Action</div>
                            <div className="add-actions">
                              {editingKey && (
                                <button onClick={cancelEdit} className="pricing-cancel" type="button" title="Cancel">
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={handleAddOrUpdate} className="pricing-plus" type="button" title={editingKey ? "Update" : "Add"}>
                                {editingKey ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          <div className="pricing-field notes-row mt-4">
                            <div className="pricing-label">Notes</div>
                            <Input
                              placeholder="Notes"
                              value={formNotes}
                              onChange={(e) => setFormNotes(e.target.value)}
                              className="w-full pricing-input"
                              size="small"
                            />
                          </div>
                        </>
                      )}

                      {/* Physio */}
                      {activeSection === "Physiotherapist" && (
                        <>
                          <div className="pricing-field">
                            <div className="pricing-label">Service</div>
                            {renderServiceInput()}
                          </div>

                          <div className="pricing-field">
                            <div className="pricing-label">Fee (in ₹)</div>
                            <InputNumber
                              placeholder="0"
                              value={formFee}
                              onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                              min={0}
                              max={MAX_FEE}
                              precision={0}
                              step={1}
                              className="w-full pricing-input"
                              controls={false}
                              size="small"
                              parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                              formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                            />
                          </div>

                          {renderDiscountField()}

                          <div className="pricing-field">
                            <div className="pricing-label opacity-0 select-none">Action</div>
                            <div className="add-actions">
                              {editingKey && (
                                <button onClick={cancelEdit} className="pricing-cancel" type="button" title="Cancel">
                                  <Ban className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={handleAddOrUpdate} className="pricing-plus" type="button" title={editingKey ? "Update" : "Add"}>
                                {editingKey ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          <div className="pricing-field notes-row">
                            <div className="pricing-label">Notes</div>
                            <Input
                              placeholder="Notes"
                              value={formNotes}
                              onChange={(e) => setFormNotes(e.target.value)}
                              className="w-full pricing-input"
                              size="small"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    <div className="pricing-strip__divider" />
                    <div className="pricing-strip__title">
                      <span>Selected Services</span>
                      {editingKey && <span className="edit-pill">Editing row</span>}
                    </div>
                  </div>

                  <Table
                    columns={columns}
                    dataSource={activeData}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: 1000 }}
                    locale={{
                      emptyText: isNurse
                        ? nurseMode === "regular"
                          ? "No regular service pricing added yet"
                          : "No one-time service pricing added yet"
                        : `No ${activeSection} pricing added yet`,
                    }}
                    className="pricing-table"
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* ✅ Bottom buttons only in EDIT mode */}
        {!readOnly && (
          <div className="shrink-0 pt-3 mt-3 border-t border-gray-200 flex justify-end gap-3 bg-white">
            <button
              onClick={handleSaveSection}
              disabled={
                !isSupportedSection ||
                saving ||
                (activeSection === "Nurse" &&
                  ((nurseMode === "regular" && nurseRegularData.length === 0) ||
                    (nurseMode === "onetime" && nurseOneTimeData.length === 0))) ||
                (activeSection === "Physiotherapist" && physioData.length === 0)
              }
              className="px-8 py-1 bg-blue-500 from-[#4951EC] to-[#00ccff] hover:opacity-90 text-white rounded-[14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-opacity shadow-sm"
            >
              {!isSupportedSection ? "Coming soon" : saving ? "Saving..." : "Save"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-8 py-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-[14px] font-medium transition-colors"
            >
              Back
            </button>
          </div>
        )}
      </div>

      {/* Proceed button only makes sense in onboarding + edit flow */}
      {!readOnly && canProceed && (
        <div className="flex justify-end mt-3">
          <button
            onClick={() => {
              secureLocalStorage.removeItem(ONBOARDING_FIRST_KEY);
              navigate("/partner/mct");
            }}
            className="px-6 py-2 bg-[#4951EC] text-white rounded-[10px] text-sm font-medium hover:bg-[#3d3acc] transition-colors shadow-sm"
          >
            Proceed to Next Step →
          </button>
        </div>
      )}
    </div>
  );
};

export default Pricing;